import React, { useState } from "react";
import BotonManualHeader from "../../components/BotonManualHeader";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  ProgressBar,
  Table,
} from "react-bootstrap";
import {
  getFirestore,
  collection,
  doc,
  setDoc,
  serverTimestamp,
} from "firebase/firestore";
import * as XLSX from "xlsx";
import { FaUpload, FaFileExcel, FaCheckCircle } from "react-icons/fa";
import firebaseApp from "../../firebase/credenciales";
import Swal from "sweetalert2";

const db = getFirestore(firebaseApp);

const ImportarMarcacion = ({ usuario }) => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [previewData, setPreviewData] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7)
  );

  // Tipos de marcación en orden
  const tiposMarcacion = [
    "Ingreso",
    "Salida",
    "Refrigerio_Inicio",
    "Refrigerio_Fin",
  ];

  // Función para procesar el archivo Excel
  const processExcelFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });

          // Tomar la primera hoja
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];

          // Convertir a JSON, ignorando la primera fila (encabezados están en la segunda fila)
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            range: 1, // Empezar desde la fila 1 (índice 0-based, fila 2 del Excel)
          });

          if (jsonData.length === 0) {
            reject(new Error("El archivo no contiene datos válidos"));
            return;
          }

          // La primera fila del jsonData son los encabezados
          const headers = jsonData[0];
          const dataRows = jsonData.slice(1);

          // Validar estructura básica
          if (headers.length < 4) {
            reject(new Error("El archivo debe tener al menos 4 columnas: Person ID, Nombres y Apellidos, Area, y días del mes"));
            return;
          }

          // Procesar datos
          const processedData = dataRows.map((row, index) => {
            if (!row[0] || !row[1]) {
              console.warn(`Fila ${index + 3} ignorada: datos incompletos`);
              return null;
            }

            const dni = row[0].toString().trim();
            const nombres = row[1].toString().trim();
            const area = row[2] ? row[2].toString().trim() : "";

            // Procesar marcaciones por día
            const marcaciones = [];
            const [year, month] = selectedMonth.split("-");

            // Columnas 3 en adelante son días del mes
            for (let colIndex = 3; colIndex < row.length; colIndex++) {
              const dayIndex = colIndex - 3; // Día del mes (0-based)
              const day = (dayIndex + 1).toString().padStart(2, "0");
              const fechaJornada = `${year}-${month}-${day}`;

              const cellValue = row[colIndex];
              if (cellValue) {
                // El valor de la celda puede contener múltiples horas separadas por espacios o comas
                const horas = cellValue.toString().split(/[\s,]+/).filter(h => h.trim());

                horas.forEach((hora, horaIndex) => {
                  if (horaIndex < tiposMarcacion.length) {
                    marcaciones.push({
                      dni,
                      nombres,
                      area,
                      fechaJornada,
                      tipo: tiposMarcacion[horaIndex],
                      hora: hora.trim(),
                      fechaHoraPeru: `${fechaJornada} ${hora.trim()}:00`,
                    });
                  }
                });
              }
            }

            return {
              dni,
              nombres,
              area,
              marcaciones,
              totalMarcaciones: marcaciones.length,
            };
          }).filter(item => item !== null);

          resolve(processedData);
        } catch (error) {
          reject(new Error(`Error al procesar el archivo: ${error.message}`));
        }
      };
      reader.onerror = () => reject(new Error("Error al leer el archivo"));
      reader.readAsArrayBuffer(file);
    });
  };

  // Función para guardar datos en Firestore
  const saveToFirestore = async (data) => {
    const totalMarcaciones = data.reduce((sum, empleado) => sum + empleado.marcaciones.length, 0);
    let processedCount = 0;

    for (const empleado of data) {
      for (const marcacion of empleado.marcaciones) {
        try {
          const registrosRef = collection(db, "asistencia", marcacion.dni, "registros");

          // Crear ID único para el registro
          const docId = `${marcacion.fechaJornada}_${marcacion.tipo}_${Date.now()}`;

          const recordData = {
            dni: marcacion.dni,
            fechaJornada: marcacion.fechaJornada,
            tipo: marcacion.tipo,
            fechaMarcacion: marcacion.hora,
            fechaHoraPeru: marcacion.fechaHoraPeru,
            ruc: usuario.ruc,
            ano: marcacion.fechaJornada.split("-")[0],
            periodo: marcacion.fechaJornada.split("-")[1],
            timestamp: serverTimestamp(),
            activo: true,
            regularizado: false,
            importado: true, // Marcar como importado desde Excel
          };

          await setDoc(doc(registrosRef, docId), recordData);

          processedCount++;
          setProgress((processedCount / totalMarcaciones) * 100);
        } catch (error) {
          console.error(`Error al guardar marcación para ${marcacion.dni}:`, error);
          throw new Error(`Error al guardar datos: ${error.message}`);
        }
      }
    }
  };

  // Manejador de cambio de archivo
  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validar tipo de archivo
    if (!selectedFile.name.endsWith('.xlsx') && !selectedFile.name.endsWith('.xls')) {
      setError("Por favor selecciona un archivo Excel (.xlsx o .xls)");
      return;
    }

    setFile(selectedFile);
    setError("");
    setSuccess("");
    setPreviewData([]);

    try {
      setLoading(true);
      const processedData = await processExcelFile(selectedFile);
      setPreviewData(processedData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Manejador de importación
  const handleImport = async () => {
    if (!previewData.length) {
      setError("No hay datos para importar");
      return;
    }

    try {
      setLoading(true);
      setProgress(0);
      setError("");

      await saveToFirestore(previewData);

      setSuccess(`Importación completada exitosamente. Se procesaron ${previewData.reduce((sum, emp) => sum + emp.marcaciones.length, 0)} marcaciones.`);

      // Limpiar estado
      setFile(null);
      setPreviewData([]);
      setProgress(0);

      // Resetear input de archivo
      const fileInput = document.getElementById("excelFile");
      if (fileInput) fileInput.value = "";

      Swal.fire({
        title: "¡Éxito!",
        text: "Los datos de marcación han sido importados correctamente",
        icon: "success",
        confirmButtonText: "Aceptar",
      });
    } catch (err) {
      setError(`Error durante la importación: ${err.message}`);
      Swal.fire({
        title: "Error",
        text: "Ocurrió un error durante la importación",
        icon: "error",
        confirmButtonText: "Aceptar",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container fluid className="py-4">
      <Card
        className="shadow-lg border-0"
        style={{ borderRadius: "15px", overflow: "hidden", minHeight: "90vh" }}
      >
        {/* Header Premium con degradado verde oscuro */}
        <Card.Header
          className="text-white py-4 position-relative"
          style={{
            background: "linear-gradient(135deg, #0d4f3c 0%, #2c7a2c 100%)",
            borderBottom: "3px solid #f39c12",
            borderRadius: "15px 15px 0 0",
          }}
        >
          <div
            className="position-absolute end-0 top-50 translate-middle-y me-4"
            style={{ zIndex: 10 }}
          >
            <BotonManualHeader usuario={usuario} />
          </div>
          <div className="text-center">
            <h2 className="fw-bold mb-1">
              <FaUpload className="me-3" />
              Importar Marcaciones
            </h2>
            <p className="mb-0 opacity-75">
              Carga masiva de marcaciones a través de archivos Excel | RUC:{" "}
              {usuario?.ruc || "–"}
            </p>
          </div>
        </Card.Header>

        <Card.Body className="p-4" style={{ backgroundColor: "#f8f9fa" }}>
          {/* Acciones Rápidas */}
          <Card
            className="border-0 mb-4"
            style={{
              background: "linear-gradient(135deg,#fffcea,#fff9db)",
              borderRadius: 14,
              boxShadow: "0 2px 12px rgba(109,40,217,.07)",
            }}
          >
            <Card.Body className="p-3 d-flex justify-content-center gap-3 flex-wrap">
              <Button
                variant="success"
                onClick={handleImport}
                disabled={loading || !previewData.length}
                className="d-flex align-items-center gap-2"
                style={{
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                  border: "none",
                  padding: "10px 20px",
                  fontWeight: "bold",
                  boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
                }}
              >
                <FaCheckCircle size="16px" />
                {loading ? "Procesando..." : "Confirmar Importación"}
              </Button>
            </Card.Body>
          </Card>

          {error && (
            <Alert variant="danger" onClose={() => setError("")} dismissible>
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" onClose={() => setSuccess("")} dismissible>
              {success}
            </Alert>
          )}

          <Row className="mb-4 g-3 align-items-center bg-white p-3 mx-0 rounded-4 shadow-sm">
            <Col md={4}>
              <Form.Group>
                <Form.Label>Período</Form.Label>
                <Form.Control
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                />
                <Form.Text className="text-muted">
                  Selecciona el mes y año correspondiente a los datos del archivo
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={8}>
              <Form.Group>
                <Form.Label>Archivo Excel</Form.Label>
                <Form.Control
                  id="excelFile"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  disabled={loading}
                />
                <Form.Text className="text-muted">
                  Selecciona un archivo Excel con la estructura: Person ID (DNI), Nombres y Apellidos, Area, Día 1, Día 2, ...
                </Form.Text>
              </Form.Group>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}

      {success && (
        <Alert variant="success" className="mt-3">
          {success}
        </Alert>
      )}

      {loading && progress > 0 && (
        <Card className="mt-3">
          <Card.Body>
            <div className="d-flex align-items-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <span>Importando datos... {Math.round(progress)}%</span>
            </div>
            <ProgressBar now={progress} className="mt-2" />
          </Card.Body>
        </Card>
      )}

      {previewData.length > 0 && (
        <Card className="mt-4">
          <Card.Header>
            <h5>Vista Previa de Datos ({previewData.length} empleados)</h5>
          </Card.Header>
          <Card.Body>
            <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
              <Table striped bordered hover size="sm">
                <thead>
                  <tr>
                    <th>DNI</th>
                    <th>Nombres y Apellidos</th>
                    <th>Área</th>
                    <th>Total Marcaciones</th>
                    <th>Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((empleado, index) => (
                    <tr key={index}>
                      <td>{empleado.dni}</td>
                      <td>{empleado.nombres}</td>
                      <td>{empleado.area}</td>
                      <td>{empleado.totalMarcaciones}</td>
                      <td>
                        <Button
                          variant="outline-info"
                          size="sm"
                          onClick={() => {
                            Swal.fire({
                              title: `Marcaciones de ${empleado.nombres}`,
                              html: empleado.marcaciones.slice(0, 10).map(m =>
                                `<div>${m.fechaJornada} - ${m.tipo}: ${m.hora}</div>`
                              ).join('') + (empleado.marcaciones.length > 10 ? '<br>...y más' : ''),
                              confirmButtonText: 'Cerrar'
                            });
                          }}
                        >
                          Ver Detalles
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="mt-3 d-flex justify-content-between align-items-center">
              <div>
                <strong>Total de empleados:</strong> {previewData.length}<br />
                <strong>Total de marcaciones:</strong> {previewData.reduce((sum, emp) => sum + emp.marcaciones.length, 0)}
              </div>
              <Button
                variant="success"
                onClick={handleImport}
                disabled={loading}
                size="lg"
              >
                <FaUpload className="me-2" />
                Importar Datos
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card className="mt-4">
        <Card.Header>
          <h5>Instrucciones de Formato</h5>
        </Card.Header>
        <Card.Body>
          <p>El archivo Excel debe tener la siguiente estructura:</p>
          <ul>
            <li><strong>Ignorar fila 1:</strong> La primera fila del archivo será ignorada</li>
            <li><strong>Fila 2 (Encabezados):</strong></li>
            <ul>
              <li>Columna A: Person ID (DNI)</li>
              <li>Columna B: Nombres y Apellidos</li>
              <li>Columna C: Area</li>
              <li>Columna D en adelante: Días del mes (Día 1, Día 2, ..., Día 31)</li>
            </ul>
            <li><strong>Filas 3 en adelante:</strong> Datos de empleados</li>
            <ul>
              <li>En cada celda de día, puedes poner múltiples horas separadas por espacios o comas</li>
              <li>Ejemplo: "08:00 12:00 13:00 17:00" (Ingreso, Refrigerio_Inicio, Refrigerio_Fin, Salida)</li>
            </ul>
          </ul>
          <Alert variant="info">
            <FaCheckCircle className="me-2" />
            Los datos importados se guardarán como registros de asistencia en la base de datos con el período seleccionado.
          </Alert>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default ImportarMarcacion;
